import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, RotateCcw, Clock, Video, ChevronDown, FastForward,
  Camera as CameraIcon, ArrowUpFromLine, Move3d, Focus,
} from 'lucide-react';
import { useBalancedEquationsStore, CameraView } from '../store/useBalancedEquationsStore';

interface Props {
  title?: string;
  experimentMode?: 'practice' | 'test';
}

export default function BalancedEquationsToolbar({
  title = 'Writing and Observing Balanced Chemical Equations',
}: Props) {
  const navigate = useNavigate();
  const [isCameraMenuOpen, setIsCameraMenuOpen] = useState(false);
  const cameraMenuRef = useRef<HTMLDivElement>(null);

  const experimentTime = useBalancedEquationsStore(s => s.experimentTime);
  const results = useBalancedEquationsStore(s => s.results);
  const currentPart = useBalancedEquationsStore(s => s.currentPart);
  const simulationSpeed = useBalancedEquationsStore(s => s.simulationSpeed);
  const cycleSimulationSpeed = useBalancedEquationsStore(s => s.cycleSimulationSpeed);
  const cameraView = useBalancedEquationsStore(s => s.cameraView);
  const setCameraView = useBalancedEquationsStore(s => s.setCameraView);
  const showTutorial = useBalancedEquationsStore(s => s.showTutorial);
  const setShowTutorial = useBalancedEquationsStore(s => s.setShowTutorial);
  const resetAll = useBalancedEquationsStore(s => s.resetAll);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cameraMenuRef.current && !cameraMenuRef.current.contains(event.target as Node)) {
        setIsCameraMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60).toString().padStart(2, '0');
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const cameraViews: { id: CameraView; label: string; icon: React.ReactNode }[] = [
    { id: 'default',   label: 'Reset View',     icon: <RotateCcw className="w-4 h-4" /> },
    { id: 'top',       label: 'Top Down',       icon: <ArrowUpFromLine className="w-4 h-4" /> },
    { id: 'side',      label: 'Side View',      icon: <Move3d className="w-4 h-4" /> },
    { id: 'closeup',   label: 'Apparatus Close-up', icon: <Focus className="w-4 h-4" /> },
    { id: 'workbench', label: 'Workbench',      icon: <CameraIcon className="w-4 h-4" /> },
  ];

  return (
    <div className="h-auto md:h-14 w-full bg-card/95 backdrop-blur-md border-b border-border flex items-center justify-between px-4 sm:px-6 py-2 md:py-0 z-50 shrink-0 shadow-sm relative gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={() => navigate('/select')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-lg transition-colors border border-transparent hover:border-border shrink-0"
        >
          <ArrowLeft className="w-4 h-4" /><span>Exit</span>
        </button>
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-card/80 hover:bg-muted/80 backdrop-blur-md border border-border rounded-lg shadow-sm shrink-0 transition-colors">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs font-bold tracking-wider uppercase">Live Lab</span>
        </div>
        <h1 className="hidden xl:block text-sm font-semibold text-foreground truncate ml-1" title={title}>{title}</h1>
      </div>

      <div className="pointer-events-auto flex-1 flex items-center justify-center gap-2 min-w-0 overflow-visible">
        <div className="overflow-x-auto no-scrollbar shrink min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-card/80 backdrop-blur-xl border border-border rounded-xl sm:rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-2xl w-max">
            <button
              onClick={() => setShowTutorial(!showTutorial)}
              className={`interactable-btn flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all duration-300 ${
                showTutorial
                  ? 'bg-blue-500/20 text-blue-500 border border-blue-500/40 shadow-[0_0_15px_rgba(59,130,246,0.3)]'
                  : 'bg-transparent border border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/80 hover:shadow-sm'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Tutorial</span>
            </button>
            <button
              onClick={resetAll}
              className="interactable-btn flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium bg-transparent border border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/80 hover:shadow-sm transition-all duration-200 group"
            >
              <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:-rotate-180 transition-transform duration-500" />
              <span className="hidden sm:inline">Reset</span>
            </button>
            <button
              onClick={cycleSimulationSpeed}
              title="Cycle simulation speed (1× / 2× / 5× / 10×)"
              className={`interactable-btn flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium border transition-all duration-200 ${
                simulationSpeed > 1
                  ? 'bg-cyan-500 text-white border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:bg-cyan-600'
                  : 'bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/80'
              }`}
            >
              <FastForward className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="font-mono text-xs tracking-widest font-bold">{simulationSpeed}×</span>
            </button>
          </div>
        </div>

        <div className="relative shrink-0 flex items-center p-1.5 sm:p-2 bg-card/80 backdrop-blur-xl border border-border rounded-xl sm:rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-2xl" ref={cameraMenuRef}>
          <button
            onClick={() => setIsCameraMenuOpen(!isCameraMenuOpen)}
            className={`interactable-btn flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
              isCameraMenuOpen ? 'bg-muted/80 text-foreground shadow-sm' : 'bg-transparent border border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/80 hover:shadow-sm'
            }`}
            title="Camera Views"
          >
            <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden sm:inline">Camera</span>
            <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${isCameraMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {isCameraMenuOpen && (
            <div className="absolute top-full mt-2 right-0 w-52 bg-card/95 backdrop-blur-xl border border-border shadow-lg rounded-xl overflow-hidden flex flex-col z-[100] animate-in fade-in zoom-in duration-200">
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">Camera Views</div>
              {cameraViews.map((view, idx) => (
                <button
                  key={view.id}
                  onClick={() => { setCameraView(view.id); setIsCameraMenuOpen(false); }}
                  className={`interactable-btn flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/80 transition-colors ${
                    idx > 0 ? 'border-t border-border/50' : ''
                  } ${cameraView === view.id ? 'text-primary bg-primary/5' : 'text-foreground'}`}
                >
                  {view.icon}<span>{view.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:flex pointer-events-auto items-center gap-2 shrink-0">
        <div className="flex items-center gap-2 bg-card/80 backdrop-blur-xl p-2 rounded-2xl border border-border shadow-sm dark:shadow-2xl">
          <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-xl border border-border">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <div className="font-mono text-foreground text-sm w-12 text-center font-bold">{formatTime(experimentTime)}</div>
          </div>
          <div className="bg-muted hover:bg-muted/80 border border-border text-foreground px-3 py-1.5 rounded-xl text-xs font-bold shadow-sm transition-colors">
            PART {currentPart} · {results.length}/3 done
          </div>
        </div>
      </div>

      <div className="md:hidden pointer-events-auto bg-muted border border-border text-foreground px-2.5 py-1.5 rounded-lg text-[10px] font-bold shrink-0">
        PART {currentPart}
      </div>
    </div>
  );
}
